import type { Preview3DConfigurationManager, SceneEditor, SceneViewer } from '@zakeke/zakeke-3d-core';
import React, { createContext, FC, useContext, useMemo } from 'react';
import { createStore, useStore } from 'zustand';
import { DesignManager } from './designManager';

type Preview3DStore = {
    saveVersion: number;
    incrementSaveVersion: () => void;

    zkProduct: MPlaza.Model | null;
    setZkProduct: (zkProduct: MPlaza.Model | null) => void;

    zkScene: Zakeke.Scene | null;
    setZkScene: (zkScene: Zakeke.Scene | null) => void;

    designManager: DesignManager | null;
    setDesignManager: (designManager: DesignManager) => void;

    /** The loaded MPlaza.Design instance for re-rendering after variant changes */
    zkDesign: MPlaza.Design | null;
    setZkDesign: (zkDesign: MPlaza.Design | null) => void;

    sceneEditor: SceneEditor | null;
    sceneManager: Preview3DConfigurationManager | null;
    sceneSelection: Zakeke.ComposerSelection | null;
    isPreview3DLoaded: boolean;

    sceneViewer: SceneViewer | null;
    setSceneViewer: (sceneViewer: SceneViewer) => void;

    sceneRefreshKey: number;
    requestSceneRefresh: () => void;
    designUpdateRequest: {
        key: number;
        variantId?: number;
        hiddenMode?: boolean;
        refresh?: boolean;
    };
    requestDesignRefresh: (variantId: number) => void;
    requestDesignHiddenMode: (hiddenMode: boolean, variantId?: number) => void;
    clearDesignUpdateRequest: (key: number) => void;

    autoscale: boolean;
    scaleValue: number | null;
    positionValue: { x: number; y: number; z: number } | null;
    setAutoscale: (autoscale: boolean) => void;
    setScaleValue: (scaleValue: number) => void;
    setPositionValue: (positionValue: { x: number; y: number; z: number }) => void;
    setSceneEditor: (sceneEditor: SceneEditor, sceneManager: Preview3DConfigurationManager, sceneSelection: Zakeke.ComposerSelection) => void;

    // Camera panel state
    isCameraPanelOpen: boolean;
    setIsCameraPanelOpen: (isOpen: boolean) => void;
    toggleCameraPanel: () => void;

    // Variant configuration mode
    variantConfigMode: 'identical' | 'sameshape' | 'different' | null;
    setVariantConfigMode: (mode: 'identical' | 'sameshape' | 'different' | null) => void;

    // Sides highlight (only show once per session)
    hasShownSidesHighlight: boolean;
    setHasShownSidesHighlight: () => void;
};

export const createPreview3DStore = () =>
    createStore<Preview3DStore>((set, get) => ({
        saveVersion: 0,
        incrementSaveVersion: () =>
            set({
                zkProduct: null,
                zkScene: null,
                isPreview3DLoaded: false,
                saveVersion: get().saveVersion + 1
            }),

        zkProduct: null,
        setZkProduct: (zkProduct) => set({ zkProduct }),

        zkScene: null,
        setZkScene: (zkScene) => set({ zkScene }),

        designManager: null,
        setDesignManager: (designManager) => set({ designManager }),

        zkDesign: null,
        setZkDesign: (zkDesign) => set({ zkDesign }),

        sceneManager: null,
        sceneSelection: null,
        isPreview3DLoaded: false,
        sceneEditor: null,
        sceneViewer: null,
        setSceneViewer: (sceneViewer) => set({ sceneViewer }),
        sceneRefreshKey: 0,
        requestSceneRefresh: () => set((state) => ({ sceneRefreshKey: state.sceneRefreshKey + 1 })),
        designUpdateRequest: {
            key: 0
        },
        requestDesignRefresh: (variantId) =>
            set((state) => {
                if (state.designUpdateRequest.variantId === variantId && state.designUpdateRequest.refresh === true) {
                    return state;
                }

                return {
                    designUpdateRequest: {
                        ...state.designUpdateRequest,
                        key: state.designUpdateRequest.key + 1,
                        variantId,
                        refresh: true
                    }
                };
            }),
        requestDesignHiddenMode: (hiddenMode, variantId) =>
            set((state) => {
                const nextVariantId = variantId ?? state.designUpdateRequest.variantId;
                if (
                    state.designUpdateRequest.variantId === nextVariantId &&
                    state.designUpdateRequest.hiddenMode === hiddenMode &&
                    state.designUpdateRequest.refresh === false
                ) {
                    return state;
                }

                return {
                    designUpdateRequest: {
                        ...state.designUpdateRequest,
                        key: state.designUpdateRequest.key + 1,
                        variantId: nextVariantId,
                        hiddenMode,
                        refresh: false
                    }
                };
            }),
        clearDesignUpdateRequest: (key) =>
            set((state) => {
                if (state.designUpdateRequest.key !== key) return state;

                return {
                    designUpdateRequest: {
                        key,
                        variantId: state.designUpdateRequest.variantId
                    }
                };
            }),
        autoscale: false,
        scaleValue: null,
        positionValue: null,
        setSceneEditor: (sceneEditor, sceneManager, sceneSelection) => set({ sceneEditor, sceneManager, sceneSelection, isPreview3DLoaded: true }),
        setAutoscale: (autoscale: boolean) => set({ autoscale }),
        setScaleValue: (scaleValue: number) => set({ scaleValue }),
        setPositionValue: (positionValue: { x: number; y: number; z: number }) => set({ positionValue }),

        // Camera panel state
        isCameraPanelOpen: false,
        setIsCameraPanelOpen: (isOpen: boolean) => set({ isCameraPanelOpen: isOpen }),
        toggleCameraPanel: () => set((state) => ({ isCameraPanelOpen: !state.isCameraPanelOpen })),

        // Variant configuration mode
        variantConfigMode: null,
        setVariantConfigMode: (mode) => set({ variantConfigMode: mode }),

        // Sides highlight (only show once per session)
        hasShownSidesHighlight: false,
        setHasShownSidesHighlight: () => set({ hasShownSidesHighlight: true })
    }));

export type Preview3DStoreType = ReturnType<typeof createPreview3DStore>;

export const Preview3DStoreContext = createContext<Preview3DStoreType | null>(null);

export const Preview3DContext: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const preview3DStore = useMemo(() => createPreview3DStore(), []);

    return <Preview3DStoreContext.Provider value={preview3DStore}>{children}</Preview3DStoreContext.Provider>;
};

const usePreview3DStore = <T extends any>(selector: (state: ReturnType<Preview3DStoreType['getState']>) => T) => {
    const store = useContext(Preview3DStoreContext);

    if (!store) throw new Error('usePreview3DStore must be used within a Preview3DStoreContext.Provider');

    const slice = useStore(store, selector);

    return slice;
};

// decommentare se si vuole loggare ogni cambiamento allo stato
// const sub = usePreview3DStore.subscribe((s) => console.log(s));

export default usePreview3DStore;
